import Utils from '../../framework/utils.js';
import DataEvent from '../../framework/data-event.js';

// PaginationView
// ==============
//
// Handles the UI of a pagination list

class PaginationView extends EventTarget {
  // `constructor(tmpl, item_tmpl, model)`
  //
  // Accepts a wapprer template, a single item template, and the underlying model

  constructor(tmpl, item_tmpl, model) {
    super();
    Utils.bindMethods(this, ['onModelChange', 'onItemClick']);

    this.dom = tmpl;
    this.item_template = item_tmpl;

    // determines how many pages are shown at a time. Total pages displayed is 1 + 2 * page_display_distance
    this.page_display_distance = 2;
    this.pagination = this.dom.querySelector('.pagination')

    this.render(model);
    model.addEventListener('Model.Change', this.onModelChange);
  }

  render(model) {
    // clear event listeners to avoid memory leak
    this.dom.querySelectorAll('.page-link').forEach((item) => {
      item.removeEventListener('click', this.onItemClick);
    })
    this.pagination.innerHTML = '';


    let displayed_page_count = 1 + 2 * this.page_display_distance;
    let start = Math.max(1, model.getCurrentPage() - this.page_display_distance);
    let end = Math.min(model.getPageCount(), start - 1 + displayed_page_count)
    if (start > 1 && end - (start - 1) < displayed_page_count) {
      start = Math.max(1, end - displayed_page_count + 1);
    }
    this.pagination.append(this._createPageItem('Previous', 'prev', { disabled: model.getCurrentPage() == 1 }));
    if (start > 1) {
      this.pagination.append(this._createPageItem(1, 'first'));
      this.pagination.insertAdjacentHTML('beforeend', '<li class="page-item disabled"><span class="page-link">&hellip;</span></li>')
    }
    for (let i = 1; i <= model.getPageCount(); i++) {
      if (i >= start && i <= end) {
        this.pagination.append(this._createPageItem(i, i, { active: i == model.getCurrentPage() }))
      }
    }
    if (end < model.getPageCount()) {
      this.pagination.insertAdjacentHTML('beforeend', '<li class="page-item disabled"><span class="page-link">&hellip;</span></li>')
      this.pagination.append(this._createPageItem(model.getPageCount(), 'last'));
    }
    this.pagination.append(this._createPageItem('Next', 'next', { disabled: model.getCurrentPage() == model.getPageCount() }));

    this.dom.querySelectorAll('.page-item:not(.disabled) .page-link').forEach((item) => {
      item.addEventListener('click', this.onItemClick);
    })
  }

  _createPageItem(label, target, options = {}) {
    let item = this.item_template.cloneNode(true);
    item.querySelector('.page-link').setAttribute('data-page', target);
    item.querySelector('.page-link').innerHTML = label;
    if (options.prefix) {
      item.querySelector('.page-item').insertAdjacentHTML('afterbegin', prefix);
    }
    if (options.disabled) {
      item.querySelector('.page-item').classList.add('disabled');
    }
    if (options.active) {
      item.querySelector('.page-item').classList.add('active');
      item.querySelector('.page-link').setAttribute('aria-current', options.active)
      item.querySelector('.page-link').setAttribute('aria-label', `Current page, page ${label}`)
    } else {
      item.querySelector('.page-link').removeAttribute('aria-current')
      item.querySelector('.page-link').setAttribute('aria-label', `go to ${ ['previous', 'next'].includes(target) ? label: '' } page ${ ['previous', 'next'].includes(target) ? '' : label }`)
    }
    return item;
  }

  onModelChange(evt) {
    this.render(evt.currentTarget);
  }

  onItemClick(evt) {
    evt.preventDefault();
    switch (evt.currentTarget.getAttribute('data-page')) {
      case 'first':
        this.dispatchEvent(new Event(PaginationView.Events.FIRST));
        break;
      case 'last':
        this.dispatchEvent(new Event(PaginationView.Events.LAST));
      break;
      case 'prev':
        this.dispatchEvent(new Event(PaginationView.Events.PREVIOUS));
      break;
      case 'next':
        this.dispatchEvent(new Event(PaginationView.Events.NEXT));
      break;
      default:
        this.dispatchEvent(new DataEvent(PaginationView.Events.SELECT, {
          page: parseInt(evt.currentTarget.getAttribute('data-page'))
        }));
      break;
    }
    return false;
  }
}

PaginationView.Events = {
  FIRST: 'PaginationView.First',
  PREVIOUS: 'PaginationView.Previous',
  SELECT: 'PaginationView.Select',
  NEXT: 'PaginationView.Next',
  LAST: 'PaginationView.Last',
};

export default PaginationView;